/**
 * ч.1403 → ч.1405 (рішення власника): перемикач режиму облікового запису у шапці — одразу після
 * логотипа, перед фільтрами організації: три іконки — «документ» (режим «ЕДО»), «особа»
 * («Користувач») і «портфель» («Бізнес»). Активний режим підсвічено і він не клікабельний;
 * «Користувач» неактивний, поки немає доступу до чужої організації; «ЕДО»/«Бізнес» — поки немає
 * власного акаунта. Клік — зміна режиму і, якщо режим належить іншому акаунту, вхід у нього
 * (useSetAccountMode).
 */
import type { AccountMode } from '@/api/auth';
import { MODE_HINT, MODE_LABEL, modeAvailable, useEffectiveMode, useModeContext, useSetAccountMode } from '@/hooks/useAccountMode';

const ICON = { width: 17, height: 17, viewBox: '0 0 24 24', fill: 'none' } as const;
const STROKE = { stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

function DocumentIcon() {
  return (
    <svg {...ICON} {...STROKE} aria-hidden>
      <path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h5M10 17h5" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg {...ICON} {...STROKE} aria-hidden>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

function BriefcaseIcon() {
  return (
    <svg {...ICON} {...STROKE} aria-hidden>
      <rect x="3" y="7.5" width="18" height="12.5" rx="2" />
      <path d="M9 7.5V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5v2M3 12.5h18" />
    </svg>
  );
}

export const MODE_ICONS: Record<AccountMode, JSX.Element> = {
  EDO: <DocumentIcon />, USER: <PersonIcon />, OWNER: <BriefcaseIcon />,
};

export default function ModeSwitch() {
  const ctxQ = useModeContext();
  const mode = useEffectiveMode();
  const switchMode = useSetAccountMode();
  const ctx = ctxQ.data;
  if (!ctx || !mode) return null;
  const btn = (m: AccountMode) => {
    const active = mode === m;
    const avail = modeAvailable(ctx, m);
    const locked = !avail.ok;
    const title = active
      ? `Поточний режим «${MODE_LABEL[m]}»: ${MODE_HINT[m]}`
      : locked ? avail.why! : `Перейти в режим «${MODE_LABEL[m]}»: ${MODE_HINT[m]}`;
    return (
      <button type="button" key={m}
        className={`app__modesw-btn${active ? ' is-active' : ''}`}
        aria-pressed={active}
        aria-label={active ? `поточний режим: ${MODE_LABEL[m]}` : `режим «${MODE_LABEL[m]}»`}
        title={title}
        disabled={active || locked || switchMode.isPending}
        onClick={() => switchMode.mutate(m)}>
        {MODE_ICONS[m]}
      </button>
    );
  };
  return (
    <span className="app__modesw" role="group" aria-label="Режим роботи">
      {btn('EDO')}{btn('USER')}{btn('OWNER')}
    </span>
  );
}
